import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { ListOrganizationUsersDto } from '@evimo/types/api/user/list-organization-users/list-organization-users.dto';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';

interface UseUserManagementTableColumnsOptions {
  onDelete?: (id: string) => void | Promise<void>;
  canDelete?: (record: ListOrganizationUsersDto) => boolean;
}

export function useUserManagementTableColumns({
  onDelete,
  canDelete,
}: UseUserManagementTableColumnsOptions = {}) {
  const { t } = useTranslation(['userManagement', 'common']);

  const columns = useMemo<ColumnsType<ListOrganizationUsersDto>>(() => {
    const baseColumns: ColumnsType<ListOrganizationUsersDto> = [
      {
        title: t('userManagement:table.email'),
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: t('userManagement:table.name'),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: t('userManagement:table.role'),
        dataIndex: 'roleName',
        key: 'roleName',
      },
      {
        title: t('userManagement:table.language'),
        dataIndex: 'language',
        key: 'language',
      },
      {
        title: t('userManagement:table.active'),
        dataIndex: 'active',
        key: 'active',
        render: (active: boolean) =>
          active ? t('common:labels.yes') : t('common:labels.no'),
      },
    ];

    if (!onDelete) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        title: t('userManagement:table.actions'),
        key: 'actions',
        align: 'center' as const,
        minWidth: 56,
        render: (_: unknown, record: ListOrganizationUsersDto) => {
          if (canDelete && !canDelete(record)) {
            return null;
          }

          return (
            <TableRowActionsMenu
              items={[
                {
                  key: 'delete',
                  label: t('common:buttons.delete'),
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: t('userManagement:deleteConfirm'),
                      okText: t('common:labels.yes'),
                      cancelText: t('common:labels.no'),
                      okButtonProps: { danger: true },
                      onOk: () => onDelete(record.id),
                    });
                  },
                },
              ]}
            />
          );
        },
      },
    ];
  }, [canDelete, onDelete, t]);

  return { columns };
}
